export interface MediaContent {
  type: string;
  url: string;
}

export interface ContentMetadata {
  author: string;
  createdAt: string;
  platform: string;
  duration?: string;
  views?: number;
}

export interface RawContent {
  id: string;
  originalUrl: string;
  type: 'twitter' | 'tiktok' | 'youtube';
  content: {
    title: string;
    description: string;
    transcript?: string;
    media?: MediaContent[];
  };
  metadata: ContentMetadata;
}

export interface KeyPoints {
  insights: string[];
  topics: string[];
}

export interface ProcessedContent {
  id: string;
  content: {
    title: string;
    description: string;
    transcript?: string;
    media?: MediaContent[];
  };
  metadata: ContentMetadata;
  keyPoints: KeyPoints;
  script?: {
    content: string;
    notes?: string[];
  };
}

export interface EnrichedContent {
  content_id: string;
  output_type: 'enriched_content';
  content: string;
  metadata: {
    topics: string[];
    sources: Array<{
      title: string;
      url: string;
      date?: string;
    }>;
    timestamp: string;
    hasUpdates: boolean;
  };
}

export interface ProcessResponse {
  success: boolean;
  id: string;
  content: string;
  metadata: {
    title?: string;
    description?: string;
    author?: string;
    publishedAt?: string;
    platform: 'youtube' | 'twitter' | 'tiktok';
    url: string;
    thumbnailUrl?: string;
    duration?: number;
    views?: number;
    likes?: number;
    comments?: number;
    shares?: number;
  };
  keyPoints: Array<{
    id: string;
    text: string;
    timestamp?: number;
    confidence: number;
  }>;
  message?: string;
}

export interface GenerationMetadata {
  wordCount: number;
  estimatedDuration?: string;
  suggestedTitle: string;
  createdAt: string;
}

export interface GeneratedContent {
  id: string;
  contentId: string;
  type: GeneratedContentTypeValue;
  content: string;
  metadata: GenerationMetadata;
}

export interface GenerationResponse {
  success: boolean;
  data?: Record<string, GeneratedContent>;
  error?: string;
  metadata?: {
    source?: string;
    requestId?: string;
    timestamp?: string;
    generatedTypes?: string[];
    errors?: Record<string, string>;
  };
}

export interface EnrichmentOption {
  id: string;
  label: string;
  description: string;
  selected: boolean;
}

export interface ProcessingState {
  youtube: boolean;
  twitter: boolean;
  tiktok: boolean;
}

export enum GeneratedContentType {
  BLOG = 'BLOG',
  SOCIAL_POST = 'SOCIAL_POST',
  YOUTUBE_SCRIPT = 'YOUTUBE_SCRIPT',
  SHORT_VIDEO_SCRIPT = 'SHORT_VIDEO_SCRIPT'
}

export type GeneratedContentTypeValue = typeof GeneratedContentType[keyof typeof GeneratedContentType];

export interface GenerationState {
  [GeneratedContentType.BLOG]: boolean;
  [GeneratedContentType.SOCIAL_POST]: boolean;
  [GeneratedContentType.YOUTUBE_SCRIPT]: boolean;
  [GeneratedContentType.SHORT_VIDEO_SCRIPT]: boolean;
}

export interface EditedContent {
  originalId: string;
  content: string;
  editedAt: string;
  type: 'enriched' | 'generated';
  generationType?: GeneratedContentTypeValue;
}

export interface CopyState {
  enriched: boolean;
  generated: Record<GeneratedContentTypeValue, boolean>;
}

export interface EnrichmentHistory {
  id: string;
  contentId: string;
  options: string[];
  result: EnrichedContent;
  timestamp: string;
  metadata?: {
    processingTime?: number;
    optionsCount: number;
    status: 'completed' | 'failed';
  };
}

export interface EnrichmentFavorite {
  id: string;
  name: string;
  options: string[];
  timestamp: string;
}

export interface ContentDraft {
  id: string;
  type: 'enriched' | 'generated';
  content: string;
  originalId: string;
  lastModified: string;
  generationType?: GeneratedContentTypeValue;
}

export interface UserPreferences {
  theme: 'light' | 'dark' | 'system';
  autoSaveDrafts: boolean;
  defaultGenerationType: GeneratedContentTypeValue;
  maxHistoryItems: number;
}

export const ContentTab = {
  ORIGINAL: 'original',
  KEY_POINTS: 'keyPoints',
  SUMMARY: 'summary',
  SCRIPT: 'script',
  ENRICHED: 'enriched',
  GENERATED: 'generated'
} as const;

export type ContentTabType = typeof ContentTab[keyof typeof ContentTab];

export interface ContentView {
  keyPoints: {
    selectedPoints: string[];
    showTargetAudience: boolean;
    showContentType: boolean;
  };
  summary: {
    expanded: boolean;
    showMetadata: boolean;
  };
  script: {
    format: 'raw' | 'formatted';
    showTimestamps: boolean;
  };
  enriched: {
    showSources: boolean;
    showTopics: boolean;
  };
  generated: {
    showMetadata: boolean;
    format: 'raw' | 'formatted';
  };
}

export interface LoadingState {
  skeleton: {
    original: boolean;
    keyPoints: boolean;
    enriched: boolean;
    generated: boolean;
    script: boolean;
    summary: boolean;
  };
  preview: boolean;
  processing: boolean;
  error: string | null;
}

export interface ComparisonState {
  enabled: boolean;
  type: 'enriched' | 'generated';
  beforeContent: string;
  afterContent: string;
}

export interface AnimationState {
  contentTransition: boolean;
  tabTransition: boolean;
  enrichmentPreview: boolean;
}

export interface PreviewState {
  isPreviewMode: boolean;
  previewContent: string | null;
  selectedOptions: string[];
}

export interface MediaLoadingState {
  isLoading: boolean;
  progress: number;
  error: string | null;
}

export interface ValidationError {
  field: string;
  message: string;
  code?: string;
}

export interface EnrichmentResponse {
  success: boolean;
  data?: EnrichedContent;
  error?: string;
  metadata?: {
    source?: string;
    requestId?: string;
    timestamp?: string;
    processingTime?: number;
  };
}

export interface EnrichmentPreviewResponse {
  success: boolean;
  data?: {
    preview: string;
    options: string[];
  };
  error?: string;
}

export interface KeyPointsResponse {
  keyPoints: KeyPoints;
  contentId: string;
}

export interface OriginalContent {
  id: string;
  url: string;
  title: string;
  content: string;
  keyPoints: KeyPoints;
  enrichedContent: EnrichedContent | null;
  generatedContent: GeneratedContent | null;
}

export interface ConcurrencyState {
  pendingRequests: {
    enrichment: string[];
    generation: string[];
  };
  lastRequest: {
    enrichment?: string;
    generation?: string;
  };
}

export interface ContentState {
  // Initial State
  originalContent: ProcessedContent | null;
  enrichedContent: EnrichedContent | null;
  generatedContent: Record<string, GeneratedContent>;
  editedContent: Record<string, EditedContent>;
  copyState: CopyState;

  // View States
  activeTab: ContentTabType;
  contentView: ContentView;

  // Processing States
  processingState: ProcessingState;
  generationState: GenerationState;
  isProcessing: boolean;
  isEnriching: boolean;
  isGenerating: boolean;
  processingProgress: number;
  validationErrors: ValidationError[];
  previewState: PreviewState;

  // UI States
  selectedContentType: GeneratedContentTypeValue | null;
  enrichmentOptions: EnrichmentOption[];
  error: string | null;

  // History and Favorites
  enrichmentHistory: EnrichmentHistory[];
  enrichmentFavorites: EnrichmentFavorite[];
  contentDrafts: Record<string, ContentDraft>;

  // User Preferences
  userPreferences: UserPreferences;

  // UI/UX States
  loadingState: LoadingState;
  comparisonState: ComparisonState;
  animationState: AnimationState;

  // Media States
  mediaContent: MediaContent[];
  mediaLoadingState: MediaLoadingState;
}

export interface ContentActions {
  setOriginalContent: (content: ProcessResponse) => void;
  setEnrichedContent: (content: EnrichedContent) => void;
  setGeneratedContent: (type: GeneratedContentTypeValue, content: GeneratedContent) => void;
  setEditedContent: (id: string, content: EditedContent) => void;
  setProcessingState: (platform: 'youtube' | 'twitter' | 'tiktok', isProcessing: boolean) => void;
  setGenerationState: (type: GeneratedContentTypeValue, isGenerating: boolean) => void;
  setSelectedContentType: (type: GeneratedContentTypeValue | null) => void;
  setActiveTab: (tab: ContentTabType) => void;
  toggleEnrichmentOption: (optionId: string) => void;
  setError: (error: string | null) => void;
  setValidationErrors: (errors: ValidationError[]) => void;
  setProcessingProgress: (progress: number) => void;
  setPreviewState: (previewState: PreviewState) => void;
  addMediaContent: (media: MediaContent) => void;
  removeMediaContent: (mediaId: string) => void;
  setLoadingState: (loadingState: LoadingState) => void;
  setComparisonState: (state: Partial<ComparisonState>) => void;
  setAnimationState: (state: Partial<AnimationState>) => void;
  setMediaLoadingState: (state: Partial<MediaLoadingState>) => void;
  reset: () => void;
}

export type ContentStore = ContentState & ContentActions;

export interface EnrichmentResponse {
  success: boolean;
  data?: EnrichedContent;
  error?: string;
  metadata?: {
    source?: string;
    requestId?: string;
    timestamp?: string;
    processingTime?: number;
  };
}

export interface EnrichmentPreviewResponse {
  success: boolean;
  data?: {
    preview: string;
    options: string[];
  };
  error?: string;
}

export interface KeyPointsResponse {
  keyPoints: KeyPoints;
  contentId: string;
}

export interface OriginalContent {
  id: string;
  url: string;
  title: string;
  content: string;
  keyPoints: KeyPoints;
  enrichedContent: EnrichedContent | null;
  generatedContent: GeneratedContent | null;
}

export type { 
  MediaContent,
  ContentMetadata,
  RawContent,
  KeyPoints,
  ProcessedContent,
  EnrichedContent,
  ProcessResponse,
  GenerationMetadata,
  GeneratedContent,
  GenerationResponse,
  EnrichmentOption,
  ProcessingState,
  GeneratedContentTypeValue,
  GenerationState,
  EditedContent,
  CopyState,
  EnrichmentHistory,
  EnrichmentFavorite,
  ContentDraft,
  UserPreferences,
  ContentTabType,
  ContentView,
  LoadingState,
  ComparisonState,
  AnimationState,
  PreviewState,
  MediaLoadingState,
  ValidationError,
  EnrichmentResponse,
  EnrichmentPreviewResponse
}; 